import { ClipboardList, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";

import { formatCountdown } from "@/hooks/useTestCountdown";
import { formatTimestamp } from "./lookups";
import {
  useAssignments,
  useAttempts,
  type ResultScope,
  type TestAssignment,
} from "@/api/tests-api";
import { Pagination } from "./Pagination";
import { pageSizeFor } from "@/lib/paging";
import { PanelConsole } from "./PanelConsole";
import { PurgeCandidateDialog } from "./PurgeCandidateDialog";

const SCOPES: { key: ResultScope; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "week", label: "This week" },
  { key: "all", label: "All" },
];

/**
 * The status filters, in the reviewer's own words.
 *
 * "Awaiting valuation" and "Scored" are NOT attempt statuses -- they ask
 * the backend's ``review_state`` filter, which reads whether an overall
 * verdict exists. Everything else maps straight onto a TestAttemptStatus.
 * At most one of ``attemptStatus`` / ``reviewState`` is set per option.
 */
type StatusFilter = {
  key: string;
  label: string;
  attemptStatus?: string;
  reviewState?: string;
};

const STATUS_FILTERS: StatusFilter[] = [
  { key: "all", label: "All" },
  // QUEUED IS NOT AN ATTEMPT QUERY, and this is the one filter that
  // cannot be. It used to ask for `attempt_status=NOT_STARTED` and
  // matched almost nothing: an attempt row is created lazily, when the
  // candidate first OPENS the exam, so the commonest queued state --
  // assigned and never opened -- has no attempt row for this list to
  // find. A reviewer saw "1 pending" on the Tests page and on the
  // dashboard, clicked Queued here, and got an empty table.
  //
  // So this key switches the data SOURCE to the assignments list, where
  // a sitting exists from the moment it is assigned, and filters on
  // `has_begun` -- the same predicate the dashboard tile counts, so the
  // number and the list agree by construction. It covers both halves:
  // never opened, and opened but never started.
  { key: "queued", label: "Queued" },
  { key: "in_progress", label: "In progress", attemptStatus: "IN_PROGRESS" },
  { key: "awaiting", label: "Awaiting valuation", reviewState: "awaiting" },
  { key: "scored", label: "Scored", reviewState: "scored" },
  { key: "expired", label: "Expired", attemptStatus: "EXPIRED" },
];

const STATUS_TONE: Record<string, string> = {
  IN_PROGRESS: "bg-status-wip-bg text-status-wip",
  SUBMITTED: "bg-status-done-bg text-status-done",
  EXPIRED: "bg-status-urgent-bg text-status-urgent",
  NOT_STARTED: "bg-status-new-bg text-status-new",
  ABANDONED: "bg-status-new-bg text-status-new",
};

/**
 * The Tests results list.
 *
 * Shows in-progress attempts alongside finished ones, with the time
 * remaining, so an admin can see who is currently sitting a test. What it
 * deliberately does not show is an unfinished candidate's work: an
 * attempt in progress is visible as a state, not as content.
 */
/**
 * Label each row with which VERSION of its exam the candidate sat.
 *
 * An exam is editable, so two candidates on the same exam may legitimately
 * have been given different case sets. That is the model working as
 * intended — but this table is where their scores get compared, so it has
 * to be visible rather than assumed.
 *
 * Nothing is labelled when an exam only ever produced one paper, which is
 * the ordinary case; a badge on every row would be noise.
 */
function versionLabels(
  rows: { test_definition_id: string | null; sealed_fingerprint: string | null }[],
): Map<string, string> {
  const byExam = new Map<string, string[]>();
  // Rows arrive newest-first, so walk them backwards: v1 must be the
  // version candidates sat FIRST, or the numbering reads inside out.
  for (const r of [...rows].reverse()) {
    if (!r.test_definition_id || !r.sealed_fingerprint) continue;
    const seen = byExam.get(r.test_definition_id) ?? [];
    if (!seen.includes(r.sealed_fingerprint)) seen.push(r.sealed_fingerprint);
    byExam.set(r.test_definition_id, seen);
  }
  const out = new Map<string, string>();
  for (const [examId, prints] of byExam) {
    if (prints.length < 2) continue; // one paper only — nothing to distinguish
    prints.forEach((fp, i) => out.set(`${examId}:${fp}`, `v${i + 1}`));
  }
  return out;
}

/**
 * Sittings that have been assigned and not attended.
 *
 * A SEPARATE TABLE, deliberately, rather than the attempts table with
 * dashes in it. None of Started / Time used / Score exists yet, and
 * neither does anything to review -- there is no recording, so the
 * review and console buttons would open on nothing. Six columns of "—"
 * and three dead buttons would say "this data failed to load" when the
 * truth is that the candidate has not turned up yet.
 */
function QueuedTable({ rows }: { rows: TestAssignment[] }) {
  if (rows.length === 0) {
    return (
      <p data-cy="results-empty" className="text-sm text-foreground-muted">
        Every assigned assessment has been attended.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-background-card shadow-theme-sm">
      <table className="w-full min-w-[44rem] text-sm">
        <thead className="border-b-2 border-border-strong bg-table-header text-left text-[12px] font-semibold uppercase tracking-[0.05em] text-table-header-text">
          <tr>
            <th className="px-4 py-3">Candidate</th>
            <th className="px-4 py-3">Assessment</th>
            <th className="px-4 py-3">State</th>
            <th className="px-4 py-3">Cases</th>
            <th className="px-4 py-3">Attempt</th>
            <th className="px-4 py-3">Assigned</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a, idx) => (
            <tr
              key={a.id}
              data-cy="queued-row"
              className={`border-t border-table-border ${
                idx % 2 === 0 ? "bg-table-row-bg" : "bg-table-row-alt"
              }`}
            >
              <td className="px-4 py-2.5 text-foreground">
                {a.candidate_name || "—"}
              </td>
              <td className="px-4 py-2.5 text-foreground">
                {a.definition_title ?? "Untitled exam"}
              </td>
              <td className="px-4 py-2.5">
                {/* The two halves of "not attended", named apart. They
                    are the same to the tile that counts them and NOT the
                    same to whoever has to chase it: one candidate has
                    never signed in, the other read the brief and stopped. */}
                <span
                  className="rounded-full bg-status-new-bg px-2 py-0.5 text-[11px] font-medium text-status-new"
                  title={
                    a.attempt_status === null
                      ? "The candidate has not opened this assessment"
                      : "The candidate opened it but never started the clock"
                  }
                >
                  {a.attempt_status === null
                    ? "not opened"
                    : "opened, not started"}
                </span>
              </td>
              <td className="px-4 py-2.5 tabular-nums text-foreground-muted">
                {a.sealed_case_count}
              </td>
              <td className="px-4 py-2.5 tabular-nums text-foreground-muted">
                {a.attempts_used + 1} of {a.max_attempts}
              </td>
              <td className="px-4 py-2.5 text-foreground-muted">
                {formatTimestamp(a.created_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function TestResultsTable({
  /** Which status filter to open on. Lets a dashboard tile deep-link
   *  into "Awaiting valuation" etc. Defaults to "all". */
  initialStatus = "all",
}: {
  initialStatus?: string;
} = {}) {
  const [scope, setScope] = useState<ResultScope>("all");
  const [statusKey, setStatusKey] = useState(initialStatus);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizeFor("results"));
  const [panel, setPanel] = useState<{
    id: string;
    name: string | null;
    attemptId: string;
    assignmentId: string | null;
    examTitle: string | null;
  } | null>(null);
  const [purging, setPurging] = useState<{
    id: string;
    username: string;
    name: string | null;
  } | null>(null);

  const trimmed = q.trim();
  const status = STATUS_FILTERS.find((s) => s.key === statusKey) ?? STATUS_FILTERS[0]!;

  // Queued reads a different table. See STATUS_FILTERS.
  const isQueued = statusKey === "queued";
  const assignments = useAssignments();
  const queuedRows = useMemo(() => {
    if (!isQueued) return [];
    const needle = trimmed.toLowerCase();
    return (assignments.data ?? [])
      .filter((a) => !a.has_begun)
      .filter(
        (a) =>
          !needle || (a.candidate_name ?? "").toLowerCase().includes(needle),
      )
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
  }, [isQueued, assignments.data, trimmed]);

  const attempts = useAttempts({
    scope,
    page,
    pageSize,
    ...(trimmed ? { q: trimmed } : {}),
    ...(status.attemptStatus ? { attemptStatus: status.attemptStatus } : {}),
    ...(status.reviewState ? { reviewState: status.reviewState } : {}),
  });
  const rows = attempts.data?.items ?? [];
  const total = isQueued ? queuedRows.length : (attempts.data?.total ?? 0);
  const loading = isQueued ? assignments.isLoading : attempts.isLoading;

  // How many sittings this list CANNOT show.
  //
  // Every filter except Queued reads attempts, and an assignment nobody
  // has opened has no attempt row -- so "All" is genuinely smaller than
  // the dashboard's "All sittings" tile, which counts assignments. The
  // gap is real and the numbers are both right; what is not acceptable
  // is leaving a reader to discover a silent difference of two and
  // decide which screen is broken. Named here instead.
  //
  // `attempt_status === null` is the exact predicate -- NOT `has_begun`.
  // An assignment whose attempt exists but never left NOT_STARTED IS in
  // the attempts list, so counting it here would over-report the gap.
  const notOpened = useMemo(
    () => (assignments.data ?? []).filter((a) => a.attempt_status === null).length,
    [assignments.data],
  );

  // Derived from the rows on THIS page. A version that only appears on
  // another page is not labelled here — the label answers "did these two
  // sit the same paper?", and the two being compared are on screen.
  const versions = versionLabels(rows);

  return (
    <div data-cy="test-results" className="flex flex-col gap-8">
      <section>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-foreground">
            Sittings ({total})
          </h3>
          <div className="flex flex-wrap items-center gap-3">
        {/* Hidden on Queued: these scope by when the sitting STARTED,
            and a queued one has not. "Today" over rows with no start
            date would either show everything or nothing, and either way
            the button would be lying about what it did. */}
        <div className={isQueued ? "hidden" : "flex gap-1"}>
          {SCOPES.map((s) => (
            <button
              key={s.key}
              type="button"
              data-cy={`results-scope-${s.key}`}
              onClick={() => {
              setPage(1);
              setScope(s.key);
            }}
              className={[
                "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                scope === s.key
                  ? "bg-accent text-accent-text"
                  : "bg-background-card text-foreground-muted hover:text-foreground",
              ].join(" ")}
            >
              {s.label}
            </button>
          ))}
        </div>
        <input
          type="search"
          data-cy="results-search"
          value={q}
          onChange={(e) => {
            setPage(1);
            setQ(e.target.value);
          }}
          placeholder="Search candidate name…"
          className="sk-input sk-sm w-64"
        />
          </div>
        </div>

        {statusKey === "all" && notOpened > 0 && (
          <p
            data-cy="results-not-opened-note"
            className="mb-3 text-xs text-foreground-muted"
          >
            {notOpened} more {notOpened === 1 ? "sitting has" : "sittings have"}{" "}
            been assigned and not opened, so {notOpened === 1 ? "it has" : "they have"}{" "}
            nothing to list here yet —{" "}
            <button
              type="button"
              data-cy="btn-see-queued"
              onClick={() => {
                setPage(1);
                setStatusKey("queued");
              }}
              className="font-semibold text-accent-hover underline underline-offset-2"
            >
              see them under Queued
            </button>
            .
          </p>
        )}

        <div className="mb-4 flex flex-wrap gap-1" data-cy="results-status-filter">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s.key}
              type="button"
              data-cy={`results-status-${s.key}`}
              onClick={() => {
                setPage(1);
                setStatusKey(s.key);
              }}
              className={[
                "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                statusKey === s.key
                  ? "border-accent bg-accent-light text-accent-hover"
                  : "border-border bg-background-card text-foreground-muted hover:text-foreground",
              ].join(" ")}
            >
              {s.label}
            </button>
          ))}
        </div>

      {loading ? (
        <p className="text-sm text-foreground-muted">Loading…</p>
      ) : isQueued ? (
        <QueuedTable rows={queuedRows} />
      ) : rows.length === 0 ? (
        <p data-cy="results-empty" className="text-sm text-foreground-muted">
          No assessments have been sat in this period.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-background-card shadow-theme-sm">
          <table className="w-full min-w-[52rem] text-sm">
            <thead className="border-b-2 border-border-strong bg-table-header text-left text-[12px] font-semibold uppercase tracking-[0.05em] text-table-header-text">
              <tr>
                <th className="px-4 py-3">Candidate</th>
                <th className="px-4 py-3">Assessment</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Progress</th>
                <th className="px-4 py-3">Started</th>
                <th className="px-4 py-3" title="How much of the allowance was used">
                  Time used
                </th>
                <th className="px-4 py-3">Score</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                // The candidate's name, the exam's name and the icon all
                // open the SAME screening. Written once: three copies of
                // this object is three chances for one of them to stop
                // passing the round, which is what keys the sheet.
                const openScreening = () =>
                  setPanel({
                    id: row.candidate_user_id,
                    name: row.candidate_name ?? row.candidate_username ?? null,
                    attemptId: row.id,
                    assignmentId: row.test_assignment_id,
                    examTitle: row.definition_title,
                  });
                return (
                <tr
                  key={row.id}
                  data-cy="result-row"
                  className={`border-t border-table-border ${
                    idx % 2 === 0 ? "bg-table-row-bg" : "bg-table-row-alt"
                  } hover:bg-table-row-hover`}
                >
                  <td className="px-4 py-2.5 text-foreground">
                    {/* A BUTTON, not a div with an onClick: the row's
                        two obvious targets have to be reachable by
                        keyboard and announced as doing something.

                        It READS as plain text until hovered -- a table
                        of underlined names is noisier than the rows are
                        worth. The focus ring carries more weight for
                        that reason: without a permanent underline it is
                        the only thing a keyboard user has. */}
                    <button
                      type="button"
                      data-cy="link-screening-candidate"
                      onClick={openScreening}
                      title="Open the full screening"
                      className="rounded text-left text-foreground hover:underline hover:underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    >
                      {row.candidate_name || row.candidate_username}
                    </button>
                  </td>
                  <td className="px-4 py-2.5">
                    <button
                      type="button"
                      data-cy="link-screening-exam"
                      onClick={openScreening}
                      title="Open the full screening"
                      className="rounded text-left text-foreground hover:underline hover:underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    >
                      {row.definition_title}
                    </button>
                    {versions.get(
                      `${row.test_definition_id}:${row.sealed_fingerprint}`,
                    ) && (
                      <span
                        data-cy="exam-version"
                        className="ml-2 rounded-full bg-background px-2 py-0.5 text-[10px] font-semibold uppercase text-status-urgent"
                        title="This exam was edited between candidates — these two did not sit the same set of cases"
                      >
                        {versions.get(
                          `${row.test_definition_id}:${row.sealed_fingerprint}`,
                        )}
                      </span>
                    )}
                    {row.dry_run_skipped && (
                      <span
                        className="ml-2 rounded-full bg-background px-2 py-0.5 text-[10px] uppercase text-foreground-muted"
                        title="The candidate skipped the practice run"
                      >
                        skipped practice
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={[
                        "rounded-full px-2 py-0.5 text-[11px] font-medium",
                        STATUS_TONE[row.status] ?? "bg-background-card",
                      ].join(" ")}
                    >
                      {row.status.replace("_", " ").toLowerCase()}
                    </span>
                    {/* The sitting's status and YOUR screening of it are
                        different facts: an attempt stays "submitted"
                        for ever, while whether you have finished
                        scoring it is per panel member. Without this the
                        list gave no way to tell which ones you had
                        already done. */}
                    {row.my_screening_complete && (
                      <span
                        data-cy="result-screened"
                        title={
                          row.my_screening_at
                            ? `You marked your screening complete on ${formatTimestamp(row.my_screening_at)}`
                            : "You marked your screening complete"
                        }
                        className="ml-2 rounded-full bg-status-done-bg px-2 py-0.5 text-[11px] font-medium text-status-done"
                      >
                        screened
                      </span>
                    )}
                    {row.status === "IN_PROGRESS" &&
                      row.seconds_remaining != null && (
                        <span
                          data-cy="result-time-left"
                          className="ml-2 font-mono text-xs tabular-nums text-foreground-muted"
                        >
                          {formatCountdown(row.seconds_remaining)} left
                        </span>
                      )}
                  </td>
                  <td className="px-4 py-2.5 tabular-nums text-foreground-muted">
                    {row.answered_count}/{row.case_count}
                  </td>
                  <td className="px-4 py-2.5 text-foreground-muted">
                    {row.started_at ? formatTimestamp(row.started_at) : "—"}
                  </td>
                  <td className="px-4 py-2.5 tabular-nums text-foreground-muted">
                    {row.time_utilised_seconds != null
                      ? formatCountdown(row.time_utilised_seconds)
                      : "—"}
                  </td>
                  <td className="px-4 py-2.5 tabular-nums text-foreground">
                    {row.overall_score != null
                      ? `${row.overall_score}${row.overall_max_score != null ? ` / ${row.overall_max_score}` : ""}`
                      : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {/* Icon-only, with aria-label AND title on each: the
                        row is otherwise unreadable to a screen reader,
                        and PURGE especially must not be a mystery glyph
                        — it deletes a candidate's recordings for good. */}
                    <div className="flex justify-end gap-1.5">
                      {/* ONE review link, not two.
                          There used to be a second button here opening a
                          drawer to listen and score, beside this one for
                          the screening sheet -- two doors into one job,
                          and the sheet's own "dictation readiness" item
                          could only be answered from behind the other
                          door. The recordings now live inside the
                          console, so this is the whole review. */}
                      <button
                        type="button"
                        data-cy="btn-panel-console"
                        onClick={openScreening}
                        aria-label={`Review ${row.candidate_name ?? row.candidate_username ?? "this candidate"}`}
                        title="Full screening — listen to the recordings, read the application, score the sheet"
                        className="rounded-md border border-border bg-background-card p-1.5 text-foreground hover:bg-table-row-hover"
                      >
                        <ClipboardList aria-hidden className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        data-cy="btn-purge-candidate"
                        aria-label={`Purge ${row.candidate_name ?? row.candidate_username ?? "this candidate"}`}
                        title="Delete this candidate's assessments and recordings"
                        onClick={() =>
                          setPurging({
                            id: row.candidate_user_id,
                            username: row.candidate_username ?? "",
                            name: row.candidate_name,
                          })
                        }
                        className="rounded-md border border-status-urgent/50 bg-status-urgent-bg p-1.5 text-status-urgent hover:bg-status-urgent/20"
                      >
                        <Trash2 aria-hidden className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Not paged on Queued: the assignments list is a single
          unpaginated call, so a pager here would be furniture that
          cannot page anything. If a practice ever queues enough sittings
          for this to matter, page the endpoint, not the array. */}
      {!isQueued && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPage={setPage}
          onPageSize={setPageSize}
          label="attempts"
        />
      )}
      </section>

      {panel && (
        <PanelConsole
          candidateId={panel.id}
          candidateName={panel.name}
          attemptId={panel.attemptId}
          examTitle={panel.examTitle}
          {...(panel.assignmentId ? { assignmentId: panel.assignmentId } : {})}
          onClose={() => setPanel(null)}
        />
      )}

      {purging && (
        <PurgeCandidateDialog
          candidateUserId={purging.id}
          candidateUsername={purging.username}
          candidateName={purging.name}
          onClose={() => setPurging(null)}
          onPurged={() => {
            void attempts.refetch();
          }}
        />
      )}
    </div>
  );
}
